import { memo, useCallback } from 'react';

import { Box } from '@/shared/ui';

import type { ReactNode } from 'react';

import type { CatalogItem } from '@/entities/catalog-item';
import type { SymbolKey } from '@/entities/symbol';
import { requestForwardCurveSelection } from '@/features/symbol-selector';
import IndeterminateCheckbox from '@/shared/ui/primitives/Checkbox/IndeterminateCheckbox';
import { useAppDispatch } from '@/store/hooks';
import useCatalogTableCell from '../useCatalogTableCell';

import type { Cell } from '@tanstack/react-table';

const factsheetCellSx = { display: 'flex', justifyContent: 'center', width: '100%' };

const ForwardCatalogCell = ({
  cell,
  symbols,
  isRestrictedData,
  isInDashboard,
  firstSelected,
  secondSelected,
  isForwardCurveLocked,
  renderFactsheetButton
}: {
  cell: Cell<CatalogItem, unknown>;
  symbols: SymbolKey[];
  isRestrictedData: boolean;
  isInDashboard: boolean;
  firstSelected: boolean;
  secondSelected: boolean;
  isForwardCurveLocked: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const onChange = useCallback(() => {
    if (!symbols.length) return;
    dispatch(requestForwardCurveSelection({ symbols }));
  }, [dispatch, symbols]);

  const renderCellContent = () => {
    switch (columnId) {
      case 'Symbol': {
        return (
          <IndeterminateCheckbox
            disabled={isRestrictedData}
            firstSelected={firstSelected}
            secondSelected={secondSelected}
            modeLocked={isForwardCurveLocked}
            onLockedClick={onChange}
            onChange={onChange}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Periods':
        return <span>{cellValue.toString().padStart(2, '0')}</span>;
      case 'Factsheet':
        return renderFactsheetButton ? (
          <Box sx={factsheetCellSx}>
            {renderFactsheetButton(cell.row.getValue('Code'), isInDashboard ? 'newTab' : 'modal')}
          </Box>
        ) : null;
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={defaultStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(ForwardCatalogCell);
